import type * as PIXI from 'pixi.js';
import { Config } from '../../video-data/config';
import type { Resolution } from '../../types';
import type { FillMode } from '../../video-data/types';

type TransformableObject = {
    position: PIXI.Point;
    scale: PIXI.Point;
};

type TransformValues = {
    tx: number;
    ty: number;
    sx: number;
    sy: number;
};

export const resetTransform = (object: TransformableObject) => {
    // console.log('resetTransform', object.name);
    const tx = object.position.x;
    const ty = object.position.y;
    const sx = object.scale.x;
    const sy = object.scale.y;

    object.position.x = 0;
    object.position.y = 0;

    // WARNING: width and height are interlinked with scale. If we change the scale
    // then we change the width and height and vice versa.
    object.scale.x = 1;
    object.scale.y = 1;

    return { tx, ty, sx, sy };
};

type TransformSettings = {
    position?: boolean;
    scale?: boolean;
};

export const setTransform = (
    object: TransformableObject,
    values: TransformValues,
    settings: TransformSettings = {},
) => {
    // console.log('setTransform ', object.name, settings);
    if (!settings.hasOwnProperty('position')) {
        settings.position = true;
    }
    if (!settings.hasOwnProperty('scale')) {
        settings.scale = true;
    }
    if (settings.position) {
        object.position.x = values.tx;
        object.position.y = values.ty;
    }
    if (settings.scale) {
        object.scale.x = values.sx;
        object.scale.y = values.sy;
    }
};

/**
 * Calculate a scaled resolution based on a specified fill mode.
 *
 * @param sourceResolution - The original resolution to scale from.
 * @param targetResolution - The target resolution to scale to.
 * @param fillMode - The fill mode for scaling: 'cover', 'contain', 'area', etc.
 * @returns object containing the scaled width, height, and scaling ratio.
 *
 * This function calculates the scaled width and height of the source resolution to fit
 * the target resolution based on the specified fill mode. The fill mode determines
 * how the source content should be scaled to cover or fit within the target resolution.
 *
 * Supported fill modes:
 * - 'cover': Scales to cover the target while maintaining the aspect ratio.
 * - 'contain': Scales to fit within the target while maintaining the aspect ratio.
 * - 'area': Scales to match the area of the source and target resolutions while maintaining the aspect ratio.
 *
 * @throws {Error} If an invalid fill mode is provided.
 */
export const calculateScaledResolution = (
    sourceResolution: Resolution,
    targetResolution: Resolution,
    fillMode: FillMode = Config.FILL_COVER,
) => {
    let ratio: number;
    const width_ratio = targetResolution.width / sourceResolution.width;
    const height_ratio = targetResolution.height / sourceResolution.height;

    switch (fillMode) {
        case Config.FILL_COVER:
            // scale based on the larger dimension ratio
            ratio = Math.max(width_ratio, height_ratio);
            break;
        case Config.FILL_CONTAIN:
            // scale based on the smaller dimension ratio
            ratio = Math.min(width_ratio, height_ratio);
            break;
        case Config.FILL_AREA:
            // scale so that the area of the source matches the area of the target (irrespective of dimensions)
            const sourceArea = sourceResolution.width * sourceResolution.height;
            const targetArea = targetResolution.width * targetResolution.height;
            ratio = Math.sqrt(targetArea / sourceArea);
            break;
        default:
            throw new Error(`invalid _fillMode: ${fillMode}`);
    }

    const width = Math.round(sourceResolution.width * ratio);
    const height = Math.round(sourceResolution.height * ratio);
    return { width, height, ratio };
};
